import React, { useEffect, useMemo, useRef } from "react";

import countiesAndLocalities from "../constants/judete.json";
import Dropdown from "./misc/Dropdown";

type CountyOption = {
  id: string;
  label: string;
  localitati: {
    nume: string;
    simplu?: string;
    comuna?: string;
  }[];
};

type LocalityOption = {
  id: string;
  label: string;
};

const EMPTY_COUNTY: CountyOption = {
  id: "-",
  label: "-",
  localitati: [],
};

const EMPTY_LOCALITY: LocalityOption = {
  id: "-",
  label: "-",
};

function CountriesAndLocalitiesDropdowns({
  values,
  errors,
  touched,
  setFieldValue,
  countiesClassName,
  citiesClassName,
  withDefaultFirstItem = false,
}: {
  values: { judet?: string; localitate?: string };
  errors: { judet?: string; localitate?: string };
  touched: { judet?: boolean; localitate?: boolean };
  setFieldValue: (field: string, value?: string | number) => void;
  countiesClassName?: string;
  citiesClassName?: string;
  withDefaultFirstItem?: boolean;
}) {
  const didInitializeDefaults = useRef(false);

  const countiesFormatted = useMemo<CountyOption[]>(() => {
    return [
      EMPTY_COUNTY,
      ...countiesAndLocalities.judete.map((county) => ({
        id: county.id,
        label: county.id,
        localitati: county.localitati,
      })),
    ];
  }, []);

  const firstRealCounty = countiesFormatted[1];

  const selectedCounty =
    countiesFormatted.find((county) => county.id === values.judet) ||
    (withDefaultFirstItem && !values.judet && firstRealCounty
      ? firstRealCounty
      : EMPTY_COUNTY);

  const localitiesFormatted = useMemo<LocalityOption[]>(() => {
    if (!selectedCounty || selectedCounty.id === "-") {
      return [EMPTY_LOCALITY];
    }

    return [
      EMPTY_LOCALITY,
      ...selectedCounty.localitati.map((locality) => ({
        label: locality.nume,
        id: locality.nume,
      })),
    ];
  }, [selectedCounty]);

  const firstRealLocality = localitiesFormatted[1];

  const selectedLocality =
    localitiesFormatted.find((locality) => locality.id === values.localitate) ||
    (withDefaultFirstItem &&
    selectedCounty.id !== "-" &&
    !values.localitate &&
    firstRealLocality
      ? firstRealLocality
      : EMPTY_LOCALITY);

  useEffect(() => {
    if (didInitializeDefaults.current) {
      return;
    }

    didInitializeDefaults.current = true;

    if (!withDefaultFirstItem) {
      return;
    }

    const countyToSet = values.judet || firstRealCounty?.id;

    if (!values.judet && countyToSet) {
      setFieldValue("judet", countyToSet);
    }

    const countyObject = countiesFormatted.find(
      (county) => county.id === countyToSet,
    );

    const firstLocalityFromCounty = countyObject?.localitati?.[0]?.nume;

    if (!values.localitate && firstLocalityFromCounty) {
      setFieldValue("localitate", firstLocalityFromCounty);
    }
  }, [
    withDefaultFirstItem,
    values.judet,
    values.localitate,
    firstRealCounty,
    countiesFormatted,
    setFieldValue,
  ]);

  return (
    <>
      <Dropdown
        title="Judet"
        data={countiesFormatted}
        singleValue={selectedCounty}
        setSingleValue={(value) => {
          const nextCountyId = value?.id;

          setFieldValue(
            "judet",
            nextCountyId === "-" ? undefined : nextCountyId,
          );

          if (withDefaultFirstItem && nextCountyId && nextCountyId !== "-") {
            const countyObject = countiesFormatted.find(
              (county) => county.id === nextCountyId,
            );
            const firstLocality = countyObject?.localitati?.[0]?.nume;

            setFieldValue("localitate", firstLocality);
          } else {
            setFieldValue("localitate", undefined);
          }
        }}
        error={errors["judet"]}
        touched={touched["judet"]}
        className={countiesClassName}
      />

      <Dropdown
        title="Localitate"
        data={localitiesFormatted}
        singleValue={selectedLocality}
        setSingleValue={(value) =>
          setFieldValue("localitate", value?.id === "-" ? undefined : value?.id)
        }
        error={errors["localitate"]}
        touched={touched["localitate"]}
        className={citiesClassName}
      />
    </>
  );
}

export default CountriesAndLocalitiesDropdowns;
